/**
 * Feature Flags Atoms
 *
 * Manages feature flags for controlled rollouts and A/B testing
 *
 * Primary responsibilities:
 * - Define feature flags
 * - Control feature availability based on user segments
 * - Support A/B testing
 *
 * Dependencies:
 * - Jotai
 * - OpenFeature
 */

import { atom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'

// Define flag keys for consistency across the application
export const FeatureFlags = {
  NEW_UI_COMPONENT: 'new-ui-component',
  HERO_TEXT_TEST: 'hero-text-test',
  PREMIUM_FEATURE: 'premium-feature',
  EXAMPLE_FLAG: 'example-flag',
  EXAMPLE_VARIANT: 'example-variant',
  EXAMPLE_LIMIT: 'example-limit',
  EXAMPLE_CONFIG: 'example-config'
}

// Define flag value types
export type HeroTextVariant = 'A' | 'B' | 'default'

// Jotai atoms for feature flags
// These provide a reactive way to access feature flags on the client side
export const flagOverridesAtom = atomWithStorage<Record<string, any>>('feature-flag-overrides', {})

// Individual flag atoms - derived from the feature flag system
export const newUIComponentFlag = atom<boolean>(false)
export const heroTextTestFlag = atom<HeroTextVariant>('default')
export const premiumFeatureFlag = atom<boolean>(false)

// Collections of flags for specific sections
export const marketingFlags = atom<Record<string, any>>({})
export const appFlags = atom<Record<string, any>>({})

// Utility function to create a number hash from a string
export function hashString(str: string): number {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash |= 0 // Convert to 32bit integer
  }
  return Math.abs(hash)
}

// Feature flag decision functions - used server-side

/**
 * Determine if the new UI component should be enabled for a user
 */
export function shouldEnableNewUI(userId?: string, isAdmin?: boolean): boolean {
  // Early return for unidentified users
  if (!userId) return false

  // Always enabled for admins
  if (isAdmin) return true

  // For regular users, rollout based on user ID hash (10% of users)
  const hash = hashString(userId)
  return hash % 100 < 10
}

/**
 * Determine which hero text variant to show a user
 */
export function getHeroTextVariant(userId?: string): HeroTextVariant {
  // Default for unidentified users
  if (!userId) return 'default'

  // Deterministic A/B test based on user ID
  const hash = hashString(userId)
  return hash % 2 === 0 ? 'A' : 'B'
}

/**
 * Determine if premium features should be enabled for a user
 */
export function shouldEnablePremiumFeature(
  userId?: string,
  isAdmin?: boolean,
  email?: string
): boolean {
  // Early return for unidentified users
  if (!userId) return false

  // Admins always get it
  if (isAdmin) return true

  // Email domain condition
  if (email?.endsWith('@company.com')) return true

  // User ID allowlist
  const allowedUsers = ['user-id-1', 'user-id-2']
  if (allowedUsers.includes(userId)) return true

  return false
}
